import type { FlowNodeEntity, FreeLayoutPluginContext } from '@flowgram.ai/free-layout-editor';
import { createAsset } from '$/api/canvasRquest';
import { persistWorkFlowCanvas } from '$/utils/nodeAutoSave';
import type { AssetCreateParams } from '$/api/server.types';
import { FlowNodeTypeEnum } from '$/domains/node/type';
import { useOfficialStore } from '$/store/useOfficialStore';
import { getSuffix } from '$/utils/nodeAddUtils';
import { getVideoCoverUrl } from '$/utils/upload';

/** 工作流节点 data / 媒体列表项上可能出现的长宽与时长字段（后端或表单可能写入） */
type WorkflowNodeMediaFields = {
  width?: unknown;
  height?: unknown;
  duration?: unknown;
  video_duration?: unknown;
};

/**
 * 将未知值解析为可用的像素尺寸（正整数）
 * @param v 宽度或高度原始值
 * @returns 合法正数时返回 Math.round 后的值，否则 undefined
 */
function toPositiveDimension(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.round(n);
}

/**
 * 将未知值解析为时长（秒，可与浮点）
 * @param v duration / video_duration 原始值
 * @returns 有限正数时返回该值，否则 undefined
 */
function toPositiveDurationSeconds(v: unknown): number | undefined {
  if (v == null || v === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return n;
}

/**
 * 合并节点级与列表首项上的宽高、时长，列表项字段优先生效，供 createAsset 使用
 * @param nodeData 节点 `data`
 * @param listItem imageList/videoList/audioList 首项（若有）
 * @returns width / height / duration 字段（有值才出现）
 */
export function mergeWorkflowMediaMeta(
  nodeData: WorkflowNodeMediaFields,
  listItem?: WorkflowNodeMediaFields | null,
): { width?: number; height?: number; duration?: number; } {
  const src: WorkflowNodeMediaFields = { ...nodeData, ...(listItem || {}) };
  const width = toPositiveDimension(src.width);
  const height = toPositiveDimension(src.height);
  const duration = toPositiveDurationSeconds(src.duration ?? src.video_duration);
  return {
    ...(width != null ? { width } : {}),
    ...(height != null ? { height } : {}),
    ...(duration != null ? { duration } : {}),
  };
}

const pickUrl = (u: string | undefined, o: string | undefined): string | undefined => (u || o || '').trim() || undefined;

/**
 * 非空时写入 `createAsset.name`，与资产库列表展示一致。
 *
 * @param name 节点媒体项上的原始文件名
 */
function pickWorkflowAssetFileName(name: string | undefined): Pick<AssetCreateParams, 'name'> | object {
  const n = (name ?? '').trim();
  return n ? { name: n } : {};
}

/**
 * 从媒体类工作流节点构造与画布资产库一致的 `createAsset` 请求体（task_type 为 workflow）。
 * 无可用媒体 URL 时返回 null（如空壳节点、仅 fileList 待上传）。
 *
 * @param node 工作流节点实体
 * @param sourceTaskId 可选，对应 task_id，写入 source_id
 * @returns createAsset 参数或 null
 */
export function buildWorkflowNodeAssetCreateParams(
  node: FlowNodeEntity,
  sourceTaskId?: string,
): AssetCreateParams | null {
  const isOversea = useOfficialStore.getState().isOversea();
  const flowType = node.flowNodeType as FlowNodeTypeEnum;
  const d = node.toJSON()?.data as {
    imageList?: { url?: string; originUrl?: string; suffix?: string; name?: string; watermark_url?: string; }[];
    videoList?: { url?: string; originUrl?: string; name?: string; watermark_url?: string; }[];
    audioList?: { url?: string; originUrl?: string; name?: string; }[];
  } & WorkflowNodeMediaFields;

  if (!d) {
    return null;
  }

  const baseSource = sourceTaskId?.trim() ? { source_id: sourceTaskId } : {};

  if (flowType === FlowNodeTypeEnum.IMAGE_NODE || flowType === FlowNodeTypeEnum.GIF_NODE) {
    const u = pickUrl(d?.imageList?.[0]?.url, d?.imageList?.[0]?.originUrl);
    if (!u) return null;
    const suffixRaw = d?.imageList?.[0]?.suffix;
    const ext = suffixRaw && String(suffixRaw).trim()
      ? String(suffixRaw).trim()
      : getSuffix(u);
    const wmList = ((d?.imageList?.[0] as { watermark_url?: string; })?.watermark_url || '').trim();
    return {
      task_type: 'workflow',
      material_type: 'image',
      url: u,
      watermark_url: wmList || u,
      ...baseSource,
      ...(ext ? { ext } : {}),
      ...mergeWorkflowMediaMeta(d, d?.imageList?.[0] as WorkflowNodeMediaFields | undefined),
      ...pickWorkflowAssetFileName(d?.imageList?.[0]?.name),
    };
  }

  if (flowType === FlowNodeTypeEnum.VIDEO_NODE) {
    const u = pickUrl(d?.videoList?.[0]?.url, d?.videoList?.[0]?.originUrl);
    if (!u) return null;
    const coverBase = isOversea
      ? ((d?.videoList?.[0]?.originUrl || '').trim() || u)
      : (d?.videoList?.[0]?.url || d?.videoList?.[0]?.originUrl || '').trim() || u;
    const thumbnail_url = getVideoCoverUrl(coverBase);
    const wmList = ((d?.videoList?.[0] as { watermark_url?: string; })?.watermark_url || '').trim();
    return {
      task_type: 'workflow',
      material_type: 'video',
      url: u,
      watermark_url: wmList || u,
      ...baseSource,
      ...(thumbnail_url ? { thumbnail_url } : {}),
      ...mergeWorkflowMediaMeta(d, d?.videoList?.[0] as WorkflowNodeMediaFields | undefined),
      ...pickWorkflowAssetFileName(d?.videoList?.[0]?.name),
    };
  }

  if (flowType === FlowNodeTypeEnum.AUDIO_NODE) {
    const u = pickUrl(d?.audioList?.[0]?.url, d?.audioList?.[0]?.originUrl);
    if (!u) return null;
    return {
      task_type: 'workflow',
      material_type: 'audio',
      url: u,
      ...baseSource,
      ...mergeWorkflowMediaMeta(d, d?.audioList?.[0] as WorkflowNodeMediaFields | undefined),
      ...pickWorkflowAssetFileName(d?.audioList?.[0]?.name),
    };
  }

  return null;
}

/**
 * 是否应在工作流媒体节点创建后自动调用资产库 createAsset（与画布 MediaInsertManager 同一开关）。
 */
function isWorkflowAssetAutoRegisterEnabled(): boolean {
  return useOfficialStore.getState().apiConfig?.agentCanvasMediaConfig?.createAssetAfterInsert ?? true;
}

/**
 * 将媒体节点登记到资产库：异步执行，不阻塞 UI；失败仅 console.warn。
 * 节点 data 已有 `assetId` 或无法解析出 URL 时会跳过。
 *
 * @param node 刚创建的媒体类节点
 * @param options.sourceTaskId 写入 createAsset.source_id（可选）
 * @param options.ctx 若传入则在写入 `assetId` 后立即触发画布保存，保证 save.json 含资产 id
 */
export function scheduleRegisterWorkflowNodeMediaAsset(
  node: FlowNodeEntity,
  options?: { sourceTaskId?: string; ctx?: FreeLayoutPluginContext; },
): void {
  if (!isWorkflowAssetAutoRegisterEnabled()) {
    return;
  }

  (async () => {
    await Promise.resolve();
    const data = node.toJSON()?.data as { assetId?: number; } | undefined;
    if (data?.assetId != null && Number.isFinite(Number(data.assetId))) {
      return;
    }

    const params = buildWorkflowNodeAssetCreateParams(node, options?.sourceTaskId);
    if (!params) {
      return;
    }

    const beforeCreate = node.toJSON()?.data as { assetId?: number; } | undefined;
    if (beforeCreate?.assetId != null && Number.isFinite(Number(beforeCreate.assetId))) {
      return;
    }

    try {
      const { asset_id } = await createAsset(params);
      const updateForm = node.form?.updateFormValues;
      const latest = node.toJSON()?.data;
      if (!updateForm || !latest) {
        return;
      }
      updateForm({ ...latest, assetId: asset_id });
      const { ctx } = options || {};
      if (ctx) {
        persistWorkFlowCanvas(ctx).catch(() => {});
      }
    } catch (e) {
      console.warn('[workflowMediaAssetRegister] createAsset failed', e);
    }
  })().catch((e) => {
    console.warn('[workflowMediaAssetRegister] schedule createAsset failed', e);
  });
}
